/**
 * Batch 18-D12 — Contextual Scorer Explanation Helper.
 *
 * Pure helper that produces the operator-visible explanation string
 * for artifacts ranked by `contextualTrainingScorer.scoreArtifactsForTicket`.
 * The result is stored on `ScoredContextualArtifact.explanation` and
 * is rendered into the ticket Training tab / schedule-aware UI as
 * the "why this matched" line for each recommendation.
 *
 * Behavior is preserved BIT-FOR-BIT from
 * `src/utils/contextualTrainingScorer.ts` prior to Batch 18-D12.
 * Every output string — including the R46-MED-4 / R46-HIGH-4 wording
 * invariants ("Mentions DTC:" instead of "Matched DTC:",
 * "Related topics:" instead of "Matched:"), the cap-at-3 keyword
 * limit, the duration format thresholds, and the `Longer option (...)`
 * wrap — is identical to the prior contextual-scorer version.
 *
 * Operator-facing copy (do NOT edit casually):
 *   - `Mentions DTC: <code list joined by ", ">`  — when matchedDTCs is non-empty
 *   - `Related topics: <up to 3 keywords joined by ", ">`  — when matchedKeywords is non-empty
 *   - `Recommended for this job type`  — fallback when only hasJobMapping fires
 *   - `Related to job category`  — source === 'domain' fallback
 *   - `General recommendation`  — source === 'general' fallback
 *   - `Relevant training`  — default source fallback (job_specific / keyword_match / vehicle_specific)
 *   - Duration: `<n> min` (when < 60) or `<round(n/60)>h` (when >= 60)
 *   - `Longer option (<duration>)`  — when isLongerFallback === true
 *   - Clauses joined by ` • ` (space-bullet-space)
 *
 * Wording invariants (audit-pinned):
 *   - FIX-R46-HIGH-4 / R47: keywords MUST be framed as "Related topics:",
 *     not "Matched:". Author-supplied keywords are free text; the
 *     "Matched:" framing can read as a prescriptive repair claim,
 *     violating the OptiBay rule that the training system guides
 *     testing, never prescribes repairs.
 *   - DTC mention MUST be framed as "Mentions DTC:", not "Matched DTC:".
 *     Same rationale: topic flag, not repair instruction.
 *
 * 18-D13: the previously-vestigial `durationBoosted` parameter has
 * been removed from this helper's signature. It was accepted at the
 * 18-D12 extraction time for behavior-preservation symmetry with the
 * prior contextual-scorer version, but was never read inside the body.
 * The two scorer locations that flowed it through (`calculateDurationScore`
 * destructuring + this call) were updated together so positional
 * arguments still align. `isLongerFallback` (the only optional
 * parameter) now sits in argument position 6.
 *
 * The `ScoredContextualArtifact` type is imported as TYPE ONLY from
 * the contextual scorer. TypeScript elides type-only imports at
 * compile time, so there is no runtime module cycle: at runtime, only
 * the scorer imports from this file. Same pattern as 18-D2 (caps.ts)
 * and 18-D8 (engineExplanation.ts).
 *
 * Out of scope:
 *   - The vehicle-prefix logic (`2018 Toyota Camry specific • ...` /
 *     `Toyota Camry relevant • ...`) lives in the scorer just AFTER
 *     this helper returns. It is NOT moved by 18-D12.
 *   - The engine-side `generateExplanation` is a separately-scoped
 *     helper at `@/utils/training/explanations/engineExplanation`
 *     with a totally different signature. NOT touched by 18-D12.
 *   - The ticket-side reason helper
 *     (`@/utils/trainingRecommendationReasons`) is a separately-scoped
 *     UI helper with its own frozen copy map. NOT touched by 18-D12.
 */

import type { TrainingArtifact } from '@/types/training';
import type { ScoredContextualArtifact } from '@/utils/contextualTrainingScorer';

/**
 * Generate human-readable explanation for why this matched
 */
export function generateContextualExplanation(
  matchedKeywords: string[],
  matchedDTCs: string[],
  hasJobMapping: boolean,
  source: ScoredContextualArtifact['source'],
  artifact: TrainingArtifact,
  isLongerFallback?: boolean
): string {
  const parts: string[] = [];

  if (matchedDTCs.length > 0) {
    // DTCs are unambiguous diagnostic codes — "Mentions DTC: P0101" reads
    // as a topic flag, not a repair instruction.
    parts.push(`Mentions DTC: ${matchedDTCs.join(', ')}`);
  }

  if (matchedKeywords.length > 0) {
    // FIX-R46-HIGH-4: keywords are author-supplied free text. The previous
    // wording "Matched: rough idle fix" could be misread as "this IS the
    // fix for rough idle" — a violation of the OptiBay philosophy that the
    // training system must guide testing/interpretation, NOT prescribe
    // repairs. Reframing as "Related topics:" makes the artifact's role
    // (subject-area guide) explicit and shifts interpretive weight from
    // the system to the technician.
    const displayKeywords = matchedKeywords.slice(0, 3);
    parts.push(`Related topics: ${displayKeywords.join(', ')}`);
  }

  if (hasJobMapping && parts.length === 0) {
    parts.push('Recommended for this job type');
  }

  if (parts.length === 0) {
    switch (source) {
      case 'domain':
        parts.push('Related to job category');
        break;
      case 'general':
        parts.push('General recommendation');
        break;
      default:
        parts.push('Relevant training');
    }
  }

  // Add duration info
  if (artifact.estimated_time_minutes) {
    const durationStr = artifact.estimated_time_minutes < 60
      ? `${artifact.estimated_time_minutes} min`
      : `${Math.round(artifact.estimated_time_minutes / 60)}h`;

    if (isLongerFallback) {
      parts.push(`Longer option (${durationStr})`);
    } else {
      parts.push(durationStr);
    }
  }

  return parts.join(' • ');
}
